const ZOOM_MEDIA_EVENT = "zoom-media:selected";

class ProductGalleryZoomElement extends HTMLElement {
  #abortController;
  #scrollTimer;

  get #dialog() {
    return this.querySelector("dialog");
  }

  get #mediaItems() {
    return [...this.querySelectorAll("[data-zoom-media]")];
  }

  get #thumbnails() {
    return [...this.querySelectorAll("[data-zoom-thumbnail]")];
  }

  connectedCallback() {
    this.disconnectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.querySelector("[data-zoom-close]")?.addEventListener("click", () => this.close(), { signal });

    this.#dialog?.addEventListener(
      "keydown",
      (e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          this.close();
        }
      },
      { signal }
    );

    this.#dialog?.addEventListener("cancel", (e) => e.preventDefault(), { signal });

    this.#thumbnails.forEach((thumb, i) => {
      thumb.addEventListener("click", () => this.#scrollToMedia(i), { signal });
    });

    this.querySelector("[data-zoom-scroll]")?.addEventListener("scroll", () => this.#onScroll(), {
      signal,
      passive: true
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = undefined;
    clearTimeout(this.#scrollTimer);
  }

  /**
   * @param {number} index
   * @param {PointerEvent} [event]
   */
  open(index, event) {
    const dialog = this.#dialog;
    if (!dialog) return;

    const mediaItems = this.#mediaItems;
    const targetMedia = mediaItems[index];
    const sourceSlide = event?.target instanceof Element ? event.target.closest("li") : null;

    const show = () => {
      dialog.showModal();
      targetMedia?.scrollIntoView({ behavior: "instant" });
      this.#setActiveThumbnail(index);
    };

    if (!document.startViewTransition || !sourceSlide || !targetMedia) {
      return show();
    }

    sourceSlide.style.viewTransitionName = "gallery-item";

    const transition = document.startViewTransition(() => {
      show();
      sourceSlide.style.viewTransitionName = "";
      targetMedia.style.viewTransitionName = "gallery-item";
    });

    transition.finished.then(() => {
      targetMedia.style.viewTransitionName = "";
    });
  }

  close() {
    const dialog = this.#dialog;
    if (!dialog?.open) return;

    this.#resetDragZoomWrappers();

    const mediaItems = this.#mediaItems;
    const activeIndex = this.#getMostVisibleIndex();
    const activeMedia = mediaItems[activeIndex];

    const carousel = this.closest("media-gallery")?.querySelector("product-gallery-carousel");
    const targetSlide = carousel?.querySelector(`.gallery__slide[data-zoom-index="${activeIndex}"]`);

    const closeDialog = () => dialog.close();

    if (!document.startViewTransition || !activeMedia || !targetSlide) {
      return closeDialog();
    }

    activeMedia.style.viewTransitionName = "gallery-item";

    const transition = document.startViewTransition(() => {
      activeMedia.style.viewTransitionName = "";
      targetSlide.style.viewTransitionName = "gallery-item";
      closeDialog();
    });

    transition.finished.then(() => {
      targetSlide.style.viewTransitionName = "";
    });
  }

  #resetDragZoomWrappers() {
    this.querySelectorAll("drag-zoom-wrapper").forEach((w) => {
      w.style.setProperty("--drag-zoom-scale", "1");
      w.style.setProperty("--drag-zoom-translate-x", "0px");
      w.style.setProperty("--drag-zoom-translate-y", "0px");
    });
  }

  #scrollToMedia(index) {
    const target = this.#mediaItems[index];
    if (!target) return;

    target.scrollIntoView({ behavior: "smooth" });
    this.#setActiveThumbnail(index);
    this.#dispatchSelect(index);
  }

  #onScroll() {
    clearTimeout(this.#scrollTimer);
    this.#scrollTimer = setTimeout(() => {
      const index = this.#getMostVisibleIndex();
      this.#setActiveThumbnail(index);
      this.#dispatchSelect(index);
    }, 50);
  }

  #getMostVisibleIndex() {
    const scrollEl = this.querySelector("[data-zoom-scroll]");
    if (!scrollEl) return 0;

    const containerRect = scrollEl.getBoundingClientRect();
    let bestIndex = 0;
    let bestRatio = 0;

    this.#mediaItems.forEach((item, i) => {
      const r = item.getBoundingClientRect();
      const visible = Math.max(0, Math.min(r.bottom, containerRect.bottom) - Math.max(r.top, containerRect.top));
      const ratio = visible / r.height;

      if (ratio > bestRatio) {
        bestRatio = ratio;
        bestIndex = i;
      }
    });

    return bestIndex;
  }

  #setActiveThumbnail(index) {
    this.#thumbnails.forEach((thumb, i) => {
      thumb.setAttribute("aria-selected", `${i === index}`);
    });

    this.#thumbnails[index]?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
  }

  #dispatchSelect(index) {
    this.dispatchEvent(new CustomEvent(ZOOM_MEDIA_EVENT, { detail: { index }, bubbles: true }));
  }
}

if (!customElements.get("product-gallery-zoom")) {
  customElements.define("product-gallery-zoom", ProductGalleryZoomElement);
}
